import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { 
  QrCode, Download, Printer, Wifi, DollarSign, 
  Type, Copy, UtensilsCrossed, Palette, Image as ImageIcon,
  RotateCcw, Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';

const QRCodeManager = ({ lojaInfo }) => {
  const [tipo, setTipo] = useState('cardapio'); // 'cardapio' | 'pix' | 'wifi' | 'custom'
  const qrCanvasRef = useRef(null);

  // Dados do QR Code
  const [numeroMesa, setNumeroMesa] = useState('');
  const [pixChave, setPixChave] = useState('');
  const [pixNome, setPixNome] = useState('');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiSenha, setWifiSenha] = useState('');
  const [wifiTipo, setWifiTipo] = useState('WPA');
  const [textoLivre, setTextoLivre] = useState('');

  // Personalização Visual da Placa
  const [formato, setFormato] = useState('mesa'); // 'mesa' (10x15cm) | 'a4' (A4 vertical)
  const [tituloDisplay, setTituloDisplay] = useState('');
  const [subtituloDisplay, setSubtituloDisplay] = useState('');
  const [rodapeDisplay, setRodapeDisplay] = useState('Conecte-se e faça seu pedido direto do celular');
  
  // Cores e Estilo
  const [corFundo, setCorFundo] = useState('#ffffff');
  const [corPrimaria, setCorPrimaria] = useState('#1e293b');
  const [corTexto, setCorTexto] = useState('#334155');
  const [corQrCode, setCorQrCode] = useState('#000000');
  const [corFundoQr, setCorFundoQr] = useState('#ffffff');
  const [bordaEstilo, setBordaEstilo] = useState('solida'); // 'solida' | 'destaque' | 'nenhuma'
  const [bgImagemUrl, setBgImagemUrl] = useState('');

  // Logo
  const BASE_URL = 'https://app.universomidiadigital.com.br';
  const logoPadrao = lojaInfo?.logo_url?.startsWith('http')
    ? lojaInfo.logo_url
    : lojaInfo?.logo_url ? `${BASE_URL}${lojaInfo.logo_url}` : '';

  const [customLogo, setCustomLogo] = useState('');
  const [incluirLogo, setIncluirLogo] = useState(true);

  const logoFinal = customLogo || logoPadrao;

  // Monta a URL do cardápio público
  const getUrlCardapio = () => {
    if (!lojaInfo?.slug) return 'https://app.universomidiadigital.com.br';
    const baseUrl = `https://app.universomidiadigital.com.br/${lojaInfo.slug}`;
    return numeroMesa ? `${baseUrl}?mesa=${encodeURIComponent(numeroMesa)}` : baseUrl;
  };

  // Valor textual do QR Code
  const getQRCodeValue = () => {
    switch (tipo) {
      case 'cardapio':
        return getUrlCardapio();
      case 'wifi':
        if (!wifiSsid) return '';
        return `WIFI:S:${wifiSsid};T:${wifiTipo};P:${wifiSenha};;`;
      case 'pix':
        return pixChave;
      case 'custom':
        return textoLivre;
      default:
        return '';
    }
  };

  const qrValor = getQRCodeValue();

  const getTituloExibicao = () => {
    if (tituloDisplay) return tituloDisplay;
    if (tipo === 'cardapio') return numeroMesa ? `MESA ${numeroMesa}` : (lojaInfo?.nome || 'CARDÁPIO DIGITAL');
    if (tipo === 'wifi') return 'WI-FI GRÁTIS';
    if (tipo === 'pix') return 'PAGAMENTO PIX';
    return lojaInfo?.nome || 'ACESSE AQUI';
  };

  const getSubtituloExibicao = () => {
    if (subtituloDisplay) return subtituloDisplay;
    if (tipo === 'cardapio') return 'Aponte a câmera do seu celular para ver o cardápio e fazer seu pedido';
    if (tipo === 'wifi') return 'Aponte a câmera para se conectar automaticamente à nossa rede';
    if (tipo === 'pix') return pixNome ? `Beneficiário: ${pixNome}` : 'Abra o app do seu banco e escaneie o código';
    return 'Aponte a câmera do celular para ler o QR Code';
  };

  // Upload local de imagem de fundo
  const handleUploadBg = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setBgImagemUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Upload local de logo
  const handleUploadLogo = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCustomLogo(reader.result);
        setIncluirLogo(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Baixa apenas o QR Code em PNG transparente ou com fundo
  const baixarApenasPNG = () => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('Gere um QR Code válido primeiro.');
      return;
    }
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `qrcode-${tipo}-${numeroMesa ? `mesa-${numeroMesa}-` : ''}${lojaInfo?.slug || 'loja'}.png`;
    link.click();
    toast.success('QR Code baixado!');
  };

  // Impressão limpa via Janela Isolada (Resolve tela em branco de layouts complexos)
  const handleImprimir = () => {
    if (!qrValor) {
      toast.error('Preencha os dados do QR Code antes de imprimir.');
      return;
    }

    const printElement = document.getElementById('printable-display-area');
    if (!printElement) return;

    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (!printWindow) {
      toast.error('Permita a abertura de pop-ups para imprimir.');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Impressão Display QR Code - ${getTituloExibicao()}</title>
          <style>
            @page {
              size: auto;
              margin: 10mm;
            }
            * {
              box-sizing: border-box;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body {
              margin: 0;
              padding: 0;
              background: #fff;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            }
            .print-wrapper {
              width: ${formato === 'mesa' ? '380px' : '620px'};
              border-radius: ${formato === 'mesa' ? '18px' : '24px'};
              overflow: hidden;
            }
          </style>
        </head>
        <body>
          <div class="print-wrapper">
            ${printElement.outerHTML}
          </div>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const copiarConteudo = () => {
    if (!qrValor) return;
    navigator.clipboard.writeText(qrValor);
    toast.success('Link / Conteúdo copiado!');
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'inherit' }}>
      
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
          <QrCode size={28} color="#2563eb" /> Estúdio de QR Code & Displays
        </h2>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '6px' }}>
          Personalize as cores, envie seu fundo, adicione identificação de mesa e imprima displays prontos para balcão e acrílico.
        </p>
      </div>

      {/* Navegação por Tipo */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {[
          { id: 'cardapio', label: 'Cardápio / Mesas', icon: <UtensilsCrossed size={16} /> },
          { id: 'wifi', label: 'Wi-Fi do Local', icon: <Wifi size={16} /> },
          { id: 'pix', label: 'Pagamento Pix', icon: <DollarSign size={16} /> },
          { id: 'custom', label: 'Link / Texto Livre', icon: <Type size={16} /> }
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => {
              setTipo(item.id);
              setTituloDisplay('');
              setSubtituloDisplay('');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '8px',
              border: tipo === item.id ? '2px solid #2563eb' : '1px solid #cbd5e1',
              backgroundColor: tipo === item.id ? '#eff6ff' : '#ffffff',
              color: tipo === item.id ? '#2563eb' : '#475569',
              fontWeight: tipo === item.id ? '700' : '500',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            {item.icon} {item.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '28px', alignItems: 'start' }}>
        
        {/* COLUNA ESQUERDA: Configuração e Customização */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Bloco 1: Conteúdo do QR */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <QrCode size={18} color="#2563eb" /> 1. Dados do QR Code
            </h3>

            {tipo === 'cardapio' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Identificação da Mesa / Local (Opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: 01, 12, Balcão, Varanda"
                    value={numeroMesa}
                    onChange={(e) => setNumeroMesa(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                  <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Adiciona o parâmetro de mesa automaticamente na URL do cardápio.
                  </span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Link Final
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={getUrlCardapio()}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#475569', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {tipo === 'wifi' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Nome da Rede Wi-Fi (SSID)</label>
                  <input
                    type="text"
                    placeholder="Ex: MinhaLoja_Clientes"
                    value={wifiSsid}
                    onChange={(e) => setWifiSsid(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Senha do Wi-Fi</label>
                  <input
                    type="text"
                    placeholder="Digite a senha"
                    value={wifiSenha}
                    onChange={(e) => setWifiSenha(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Tipo de Criptografia</label>
                  <select
                    value={wifiTipo}
                    onChange={(e) => setWifiTipo(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  >
                    <option value="WPA">WPA / WPA2 / WPA3</option>
                    <option value="WEP">WEP</option>
                    <option value="nopass">Sem Senha (Aberta)</option>
                  </select>
                </div>
              </div>
            )}

            {tipo === 'pix' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Nome do Recebedor</label>
                  <input
                    type="text"
                    placeholder="Ex: Seu Restaurante LTDA"
                    value={pixNome}
                    onChange={(e) => setPixNome(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Código Pix Copia e Cola / Chave</label>
                  <textarea
                    rows={3}
                    placeholder="Cole aqui a chave Pix ou o payload completo copia-e-cola"
                    value={pixChave}
                    onChange={(e) => setPixChave(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {tipo === 'custom' && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Texto ou Link</label>
                <input
                  type="text"
                  placeholder="https://suapagina.com ou qualquer texto"
                  value={textoLivre}
                  onChange={(e) => setTextoLivre(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>
            )}
          </div>

          {/* Bloco 2: Design e Aparência */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Palette size={18} color="#2563eb" /> 2. Personalização do Layout da Placa
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              
              {/* Formato de Impressão */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>Formato do Display</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setFormato('mesa')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: formato === 'mesa' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                      backgroundColor: formato === 'mesa' ? '#eff6ff' : '#fff',
                      color: formato === 'mesa' ? '#2563eb' : '#475569',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Display de Mesa (10x15cm)
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormato('a4')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: formato === 'a4' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                      backgroundColor: formato === 'a4' ? '#eff6ff' : '#fff',
                      color: formato === 'a4' ? '#2563eb' : '#475569',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Totem / Folha A4
                  </button>
                </div>
              </div>

              {/* Textos Personalizados */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>Título Principal</label>
                <input
                  type="text"
                  placeholder={getTituloExibicao()}
                  value={tituloDisplay}
                  onChange={(e) => setTituloDisplay(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>Instrução / Subtítulo</label>
                <input
                  type="text"
                  placeholder={getSubtituloExibicao()}
                  value={subtituloDisplay}
                  onChange={(e) => setSubtituloDisplay(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '4px' }}>Rodapé</label>
                <input
                  type="text"
                  value={rodapeDisplay}
                  onChange={(e) => setRodapeDisplay(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>

              {/* Paleta de Cores */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Cores do Display</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="color" value={corFundo} onChange={(e) => setCorFundo(e.target.value)} style={{ width: '32px', height: '32px', border: 'none', cursor: 'pointer' }} />
                    <span style={{ fontSize: '12px', color: '#475569' }}>Fundo Card</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="color" value={corPrimaria} onChange={(e) => setCorPrimaria(e.target.value)} style={{ width: '32px', height: '32px', border: 'none', cursor: 'pointer' }} />
                    <span style={{ fontSize: '12px', color: '#475569' }}>Cor Título</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="color" value={corTexto} onChange={(e) => setCorTexto(e.target.value)} style={{ width: '32px', height: '32px', border: 'none', cursor: 'pointer' }} />
                    <span style={{ fontSize: '12px', color: '#475569' }}>Cor Textos</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="color" value={corQrCode} onChange={(e) => setCorQrCode(e.target.value)} style={{ width: '32px', height: '32px', border: 'none', cursor: 'pointer' }} />
                    <span style={{ fontSize: '12px', color: '#475569' }}>Cor QR Code</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="color" value={corFundoQr} onChange={(e) => setCorFundoQr(e.target.value)} style={{ width: '32px', height: '32px', border: 'none', cursor: 'pointer' }} />
                    <span style={{ fontSize: '12px', color: '#475569' }}>Fundo QR</span>
                  </div>
                </div>
              </div>

              {/* Uploads de Imagens (Fundo & Logo) */}
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Imagem de Fundo do Display (Opcional)
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadBg}
                      style={{ fontSize: '12px' }}
                    />
                    {bgImagemUrl && (
                      <button
                        type="button"
                        onClick={() => setBgImagemUrl('')}
                        style={{ padding: '4px 8px', fontSize: '11px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Remover Fundo
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Logo Central do QR Code
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadLogo}
                      style={{ fontSize: '12px' }}
                    />
                    {logoFinal && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#334155', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={incluirLogo}
                          onChange={(e) => setIncluirLogo(e.target.checked)}
                        />
                        Exibir Logo no centro do QR
                      </label>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* COLUNA DIREITA: Preview do Display em Tempo Real & Botões de Ação */}
        <div style={{ position: 'sticky', top: '20px' }}>
          
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            
            {/* DISPLAY ELEMENT (Este é o elemento renderizado e enviado à janela de impressão) */}
            <div 
              id="printable-display-area"
              style={{
                width: formato === 'mesa' ? '340px' : '480px',
                minHeight: formato === 'mesa' ? '480px' : '620px',
                backgroundColor: corFundo,
                backgroundImage: bgImagemUrl ? `url(${bgImagemUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '20px',
                border: `3px solid ${corPrimaria}`,
                padding: '30px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                textAlign: 'center',
                boxShadow: '0 12px 30px -8px rgba(0, 0, 0, 0.15)',
                position: 'relative',
                boxSizing: 'border-box'
              }}
            >
              {/* Header do Card */}
              <div style={{ width: '100%' }}>
                {logoFinal && (
                  <img 
                    src={logoFinal} 
                    alt="Logo" 
                    style={{ 
                      maxHeight: formato === 'mesa' ? '46px' : '60px', 
                      maxWidth: '160px', 
                      objectFit: 'contain', 
                      margin: '0 auto 10px auto', 
                      display: 'block' 
                    }} 
                  />
                )}
                
                <h1 style={{ 
                  fontSize: formato === 'mesa' ? '20px' : '26px', 
                  fontWeight: '900', 
                  color: corPrimaria, 
                  margin: '0 0 6px 0', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  lineHeight: '1.2'
                }}>
                  {getTituloExibicao()}
                </h1>

                <p style={{ 
                  fontSize: formato === 'mesa' ? '12px' : '14px', 
                  color: corTexto, 
                  margin: '0 auto', 
                  maxWidth: '280px', 
                  lineHeight: '1.3',
                  fontWeight: '500'
                }}>
                  {getSubtituloExibicao()}
                </p>
              </div>

              {/* QR Code Container */}
              <div 
                ref={qrCanvasRef} 
                style={{ 
                  padding: '14px', 
                  background: corFundoQr, 
                  borderRadius: '16px', 
                  border: '1px solid #e2e8f0', 
                  display: 'inline-block',
                  margin: '18px 0',
                  boxShadow: '0 6px 12px -2px rgba(0, 0, 0, 0.08)'
                }}
              >
                {qrValor ? (
                  <QRCodeCanvas
                    value={qrValor}
                    size={formato === 'mesa' ? 180 : 230}
                    bgColor={corFundoQr}
                    fgColor={corQrCode}
                    level="H"
                    includeMargin={true}
                    imageSettings={
                      incluirLogo && logoFinal
                        ? {
                            src: logoFinal,
                            height: formato === 'mesa' ? 36 : 46,
                            width: formato === 'mesa' ? 36 : 46,
                            excavate: true,
                          }
                        : undefined
                    }
                  />
                ) : (
                  <div style={{ width: '180px', height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '13px' }}>
                    Configure os dados ao lado
                  </div>
                )}
              </div>

              {/* Informações Extras (Wi-Fi / Pix / Rodapé) */}
              <div style={{ width: '100%' }}>
                {tipo === 'wifi' && wifiSsid && (
                  <div style={{ 
                    marginBottom: '10px', 
                    padding: '8px 14px', 
                    background: 'rgba(255, 255, 255, 0.9)', 
                    borderRadius: '8px', 
                    border: '1px solid #e2e8f0', 
                    fontSize: '12px', 
                    color: '#1e293b',
                    display: 'inline-block'
                  }}>
                    <div><strong>Rede:</strong> {wifiSsid}</div>
                    {wifiSenha && <div><strong>Senha:</strong> {wifiSenha}</div>}
                  </div>
                )}

                <div style={{ 
                  fontSize: '11px', 
                  color: corTexto, 
                  fontWeight: '600',
                  opacity: 0.85,
                  letterSpacing: '0.2px'
                }}>
                  {rodapeDisplay}
                </div>

                <div style={{ 
                  marginTop: '10px', 
                  borderTop: '1px dashed rgba(0,0,0,0.15)', 
                  paddingTop: '8px', 
                  fontSize: '9px', 
                  color: corTexto, 
                  opacity: 0.6 
                }}>
                  {lojaInfo?.nome || 'Universo Mídia Digital'}
                </div>
              </div>
            </div>
          </div>

          {/* Botões de Ação */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              type="button"
              onClick={handleImprimir}
              disabled={!qrValor}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '14px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#fff',
                fontWeight: '700',
                fontSize: '15px',
                cursor: qrValor ? 'pointer' : 'not-allowed',
                opacity: qrValor ? 1 : 0.5,
                boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)'
              }}
            >
              <Printer size={20} /> Imprimir Placa / Display
            </button>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <button
                type="button"
                onClick={baixarApenasPNG}
                disabled={!qrValor}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#334155',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: qrValor ? 'pointer' : 'not-allowed',
                  opacity: qrValor ? 1 : 0.5
                }}
              >
                <Download size={16} /> Baixar QR (PNG)
              </button>

              <button
                type="button"
                onClick={copiarConteudo}
                disabled={!qrValor}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#334155',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: qrValor ? 'pointer' : 'not-allowed',
                  opacity: qrValor ? 1 : 0.5
                }}
              >
                <Copy size={16} /> Copiar Link
              </button>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default QRCodeManager;